import React, { useState, useEffect, useMemo } from "react";
import { Checkbox, Spin } from "antd";
import { toast } from "react-toastify";
import { getEnvPoints } from "../../../../actions/specManager";
import {
  filterSpecManagerLocationsBySearch,
  mapEnvPointToLocationRow,
  mapEnvLocationToProductShape,
  SPEC_TYPE,
} from "../utils/utils";
import SelectedProductList from "../shared/SelectedProductList";
import AllProductsList from "../shared/AllProductsList";

export default function UpdateLocationListPanel({
  customer,
  identifierFields,
  selectedLocations,
  searchValue,
  onSelectedLocationsChange,
}) {
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();
    getEnvPoints({}, abortController.signal, customer).then((response) => {
      if (response.success) {
        setAllLocations(response.locations.map((point) => (
          mapEnvPointToLocationRow(point, identifierFields)
        )));
      } else if (!response.canceled) {
        toast.error(response.message);
      }
      setLoading(false);
    });
    return () => abortController.abort();
  }, [customer, identifierFields]);

  const selectedLocationIds = useMemo(
    () => new Set(selectedLocations.map((locationRow) => String(locationRow.location_id))),
    [selectedLocations],
  );

  const filteredAll = filterSpecManagerLocationsBySearch(
    allLocations,
    searchValue,
    identifierFields,
  ).map((locationRow) => mapEnvLocationToProductShape(locationRow, identifierFields));

  const selectedLocationsForList = selectedLocations.map(
    (locationRow) => mapEnvLocationToProductShape(locationRow, identifierFields),
  );

  const selectableFiltered = filteredAll.filter(
    (row) => !selectedLocationIds.has(String(row.product_id)),
  );

  const selectAllChecked = filteredAll.length > 0 && selectableFiltered.length === 0;

  const handleAddLocation = (productShapedRow) => {
    const locationId = String(productShapedRow.product_id);
    if (selectedLocationIds.has(locationId)) return;
    const source = allLocations.find((row) => String(row.location_id) === locationId);
    if (!source) return;
    onSelectedLocationsChange([...selectedLocations, source]);
  };

  const handleRemoveLocation = (productId) => {
    const locationToRemove = selectedLocations.find(
      (row) => String(row.location_id) === String(productId),
    );
    if (locationToRemove && locationToRemove.test_submitted) {
      toast.error("Cannot remove a location with submitted tests.");
      return;
    }
    onSelectedLocationsChange(
      selectedLocations.filter((row) => String(row.location_id) !== String(productId)),
    );
  };

  const handleToggleChecked = (productShapedRow, nextChecked) => {
    if (nextChecked) {
      handleAddLocation(productShapedRow);
    }
  };

  const handleSelectAllChange = (event) => {
    if (!event.target.checked) return;
    const toAdd = selectableFiltered
      .map((row) => allLocations.find((loc) => String(loc.location_id) === String(row.product_id)))
      .filter(Boolean);
    if (toAdd.length) {
      onSelectedLocationsChange([...selectedLocations, ...toAdd]);
    }
  };

  return (
    <div className="UpdateProductList_Stacked">
      <div className="UpdateProductList_SelectedSection">
        <div className="UpdateProductList_SectionTitle">
          Selected Locations (
          {selectedLocations.length}
          )
        </div>
        <div className="UpdateProductList_SelectedScroll">
          <SelectedProductList
            products={selectedLocationsForList}
            identifierFields={identifierFields}
            loading={loading}
            specType={SPEC_TYPE.ENVIRONMENT}
            onRemoveProduct={handleRemoveLocation}
            removeBlockedTooltip="This location is under testing and cannot be removed."
          />
        </div>
      </div>

      <div className="UpdateProductList_AllSection">
        <div className="UpdateProductList_AllHeader">
          <div className="UpdateProductList_SectionTitle">All Locations</div>
          <Checkbox
            checked={selectAllChecked}
            disabled={loading || selectableFiltered.length === 0}
            onChange={handleSelectAllChange}
          >
            <span className="UpdateProductList_SelectAllLabel">Select All</span>
          </Checkbox>
        </div>
        <div className="UpdateProductList_AllScroll">
          {loading ? (
            <div className="UpdateProductList_Loading"><Spin /></div>
          ) : (
            <AllProductsList
              products={filteredAll}
              identifierFields={identifierFields}
              specType={SPEC_TYPE.ENVIRONMENT}
              loading={loading}
              selectedProductIds={selectedLocationIds}
              onToggleChecked={handleToggleChecked}
            />
          )}
        </div>
      </div>
    </div>
  );
}
